/**
 * A local model through Ollama's native chat API (`POST /api/chat`). No key;
 * the browser must be allowed to reach it, which Ollama grants per origin
 * (`OLLAMA_ORIGINS`). localhost pages are allowed by default, a hosted page
 * such as app.genie.art is not.
 *
 * Native, not the OpenAI-compatible `/v1`: only the native API sets the context
 * window. Ollama's default is 4k tokens on most Macs, smaller than the system
 * prompt plus an edit plus the answer, and when it overflows Ollama silently
 * drops the oldest text (the system prompt that defines the answer format).
 * So every request sizes `num_ctx` to fit, and sends `truncate: false` /
 * `shift: false` so an overflow is an error instead of a quietly worse answer.
 */

import { DEFAULT_LOCAL_BASE_URL, ShaderGenerationError } from '../types'
import { abortedError, httpError, toShaderError } from './errors'
import { getJson } from './openai-compatible'
import type { ChatTransportRequest, ShaderModelOption } from './openai-compatible'
import { readLines, unfinishedStreamError } from './sse'

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1'])

/** Output budget: a long sketch is ~12k characters, about 4k tokens. */
export const LOCAL_NUM_PREDICT = 4096
/** Context window bounds. Powers of two in between, so the model is not reloaded for every size. */
export const LOCAL_MIN_NUM_CTX = 8192
export const LOCAL_MAX_NUM_CTX = 32768
/** Code and English both run near 3-4 characters a token; 3 errs on the roomy side. */
const CHARS_PER_TOKEN = 3

function normalizeBaseUrl(baseUrl: string | undefined): string {
  return (baseUrl?.trim() || DEFAULT_LOCAL_BASE_URL).replace(/\/+$/, '')
}

/** The page's origin, or null outside a browser. */
function pageOrigin(): string | null {
  return typeof window === 'undefined' || !window.location ? null : window.location.origin
}

function isLocalOrigin(origin: string | null): boolean {
  if (!origin) return true
  try {
    return LOCAL_HOSTS.has(new URL(origin).hostname)
  } catch {
    return false
  }
}

/**
 * A fetch `TypeError` means Ollama is not running, or (from a hosted page) that
 * it refused this origin. The browser does not say which, so a hosted page gets
 * the CORS fix and a localhost page the "start it" fix.
 */
export function localNetworkError(baseUrl: string): ShaderGenerationError {
  const origin = pageOrigin()
  if (origin && !isLocalOrigin(origin)) {
    return new ShaderGenerationError(
      `This page (${origin}) is not allowed to reach Ollama at ${baseUrl}. Set OLLAMA_ORIGINS=${origin} in Ollama's environment and restart Ollama (on macOS: launchctl setenv OLLAMA_ORIGINS "${origin}", then quit and reopen Ollama).`,
      'CORS',
    )
  }
  return new ShaderGenerationError(`Could not reach Ollama at ${baseUrl}. Start Ollama (open the app or run "ollama serve") and try again.`, 'NETWORK')
}

// ---------------------------------------------------------------------------
// Context sizing
// ---------------------------------------------------------------------------

export interface LocalContextSize {
  numCtx: number
  numPredict: number
}

function estimatePromptTokens(request: Pick<ChatTransportRequest, 'system' | 'messages'>): number {
  const chars = request.system.length + request.messages.reduce((sum, m) => sum + m.content.length, 0)
  return Math.ceil(chars / CHARS_PER_TOKEN)
}

/**
 * The window a request needs: its prompt (system + every message, estimated at
 * 3 characters a token) plus the output budget, rounded up to a power of two
 * within [8192, 32768] and never above what the model supports. When the window
 * is capped, the output budget shrinks to what is left rather than overflowing.
 */
export function localContextSize(
  request: Pick<ChatTransportRequest, 'system' | 'messages' | 'maxTokens'>,
  modelContextLength?: number,
): LocalContextSize {
  const promptTokens = estimatePromptTokens(request)
  const wantPredict = Math.min(request.maxTokens, LOCAL_NUM_PREDICT)
  let numCtx = LOCAL_MIN_NUM_CTX
  while (numCtx < promptTokens + wantPredict && numCtx < LOCAL_MAX_NUM_CTX) numCtx *= 2
  if (modelContextLength && modelContextLength > 0) numCtx = Math.min(numCtx, modelContextLength)
  // Keep at least 1024 tokens to answer; if even that does not fit, Ollama refuses the prompt (truncate: false).
  const numPredict = Math.min(wantPredict, Math.max(1024, numCtx - promptTokens))
  return { numCtx, numPredict }
}

interface OllamaShowResponse {
  model_info?: Record<string, unknown>
}

/** Model context lengths by `${base}|${model}`; a model's does not change while it is pulled. */
const contextLengths = new Map<string, number | null>()

/** The model's trained context length from `/api/show`, or undefined when Ollama will not say. */
async function modelContextLength(base: string, model: string, signal: AbortSignal): Promise<number | undefined> {
  const key = `${base}|${model}`
  if (contextLengths.has(key)) return contextLengths.get(key) ?? undefined
  let length: number | null = null
  try {
    const response = await fetch(`${base}/api/show`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model }),
      signal,
    })
    if (!response.ok) return undefined
    const body = (await response.json()) as OllamaShowResponse
    for (const [name, value] of Object.entries(body.model_info ?? {})) {
      if (name.endsWith('.context_length') && typeof value === 'number' && value > 0) length = value
    }
  } catch {
    if (signal.aborted) throw abortedError()
    // Best effort: the chat request reports an unreachable Ollama properly.
    return undefined
  }
  contextLengths.set(key, length)
  return length ?? undefined
}

/** Forgets cached model context lengths (tests, or after re-pulling a model). */
export function clearLocalModelInfoCache(): void {
  contextLengths.clear()
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

interface OllamaChatLine {
  message?: { content?: string; thinking?: string }
  done?: boolean
  done_reason?: string
  error?: string
}

const CONTEXT_OVERFLOW = /context (length|window|size)|exceeds|too long|truncat/i

function contextFullError(numCtx: number): ShaderGenerationError {
  return new ShaderGenerationError(
    `This request does not fit the model's context window (${numCtx.toLocaleString('en-US')} tokens), so the shader could not be finished. Shorten the shader or the request, or pick a model with a longer context.`,
    'TRUNCATED',
  )
}

function outputLimitError(): ShaderGenerationError {
  return new ShaderGenerationError(
    'The local model ran out of output tokens before the shader was finished. Ask for a simpler effect, or pick a model that does not spend its budget on reasoning.',
    'TRUNCATED',
  )
}

/** Reads Ollama's NDJSON stream; only `message.content` is the answer (reasoning arrives as `thinking`). */
async function readOllamaStream(response: Response, request: ChatTransportRequest, size: LocalContextSize): Promise<string> {
  let text = ''
  // Set inside the callback, so declared wide: TS would otherwise narrow it to null.
  let doneReason = null as string | null
  await readLines(response, request.signal, (raw) => {
    const line = raw.trim()
    if (!line) return
    let chunk: OllamaChatLine
    try {
      chunk = JSON.parse(line) as OllamaChatLine
    } catch {
      return
    }
    if (chunk.error) {
      throw CONTEXT_OVERFLOW.test(chunk.error) ? contextFullError(size.numCtx) : new ShaderGenerationError(`Ollama: ${chunk.error}`, 'UNKNOWN')
    }
    const delta = chunk.message?.content
    if (delta) {
      text += delta
      request.onText?.(text)
    }
    if (chunk.done) {
      doneReason = chunk.done_reason ?? 'stop'
      return 'stop'
    }
  })
  if (doneReason === null) throw unfinishedStreamError()
  if (doneReason === 'length') {
    // Out of window or out of output budget; the budget is the usual one.
    throw estimatePromptTokens(request) + size.numPredict > size.numCtx ? contextFullError(size.numCtx) : outputLimitError()
  }
  return text
}

export async function completeLocal(baseUrl: string | undefined, request: ChatTransportRequest): Promise<string> {
  const base = normalizeBaseUrl(baseUrl)
  const { signal } = request
  if (signal.aborted) throw abortedError()
  try {
    const size = localContextSize(request, await modelContextLength(base, request.model, signal))
    const response = await fetch(`${base}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: request.model,
        messages: [{ role: 'system', content: request.system }, ...request.messages],
        stream: true,
        options: { num_ctx: size.numCtx, num_predict: size.numPredict, temperature: request.temperature },
        // An overflow is an error, never a silently dropped system prompt.
        truncate: false,
        shift: false,
      }),
      signal,
    })
    if (!response.ok) {
      const error = await httpError(response, 'Ollama')
      if (error.code === 'UNKNOWN' && /not found|model is required/i.test(error.message)) {
        throw new ShaderGenerationError(`${error.message}. Pull it with "ollama pull ${request.model}".`, 'UNKNOWN')
      }
      if (error.code === 'UNKNOWN' && CONTEXT_OVERFLOW.test(error.message)) throw contextFullError(size.numCtx)
      throw error
    }
    return await readOllamaStream(response, request, size)
  } catch (error) {
    throw toShaderError(error, { onNetworkError: () => localNetworkError(base) })
  }
}

interface OllamaTagsResponse {
  models?: Array<{ name?: string; model?: string; details?: { parameter_size?: string } }>
}

/** Models pulled into this Ollama. */
export async function listLocalModels(baseUrl: string | undefined, signal?: AbortSignal): Promise<ShaderModelOption[]> {
  const base = normalizeBaseUrl(baseUrl)
  const body = await getJson<OllamaTagsResponse>({ url: `${base}/api/tags`, label: 'Ollama', onNetworkError: () => localNetworkError(base) }, signal)
  return (body.models ?? []).flatMap((model) => {
    const id = model.name ?? model.model
    if (!id) return []
    const size = model.details?.parameter_size
    return [{ id, label: size ? `${id} (${size})` : id }]
  })
}
